import React, { FC, useEffect } from "react";

import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormControl, FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";

import { DateInput, FieldError, GridLineBreak, InputLabel, TextInput } from "@/components/atoms";
import {
    CommunicationType,
    getSignAddress,
} from "@/components/features/ToDos/CustomerCardPage/steps/CommunicationStep/formType";
import { StyledCustomerTypography } from "@/components/features/ToDos/CustomerCardPage/styled";
import { FormPanel, StepComponentProps } from "@/components/organisms";

import { CustomerCardFormType } from "../../formType";

const CommunicationStep: FC<StepComponentProps> = () => {
    const { t } = useTranslation();

    const {
        formState: { errors },
        control,
        getValues,
        setValue,
    } = useFormContext<CustomerCardFormType>();
    const isPhysicalPerson = getValues("isPhysicalPerson");
    const personal = getValues("personal");

    useEffect(() => {
        if (getValues("officeCommunication.address") === "") {
            setValue("officeCommunication.address", getSignAddress(personal));
        }
    }, []);

    return (
        <FormPanel title={t("CustomerCard.General.CommunicationWithOurOffice")} mode="edit">
            <FormControl error={Boolean(errors.officeCommunication?.channel)} className="mb10">
                <InputLabel
                    required
                    htmlFor="officeCommunication.channel"
                    text={t("CustomerCard.Communication.InformationReception")}
                />
                <Controller
                    name="officeCommunication.channel"
                    control={control}
                    render={({ field }) => (
                        <RadioGroup {...field}>
                            <FormControlLabel
                                value={CommunicationType.PAPER}
                                label={t("CustomerCard.Communication.Paper")}
                                control={
                                    <Radio
                                        data-testid="radio-paper"
                                        inputProps={{
                                            // Necessary, waiting https://github.com/mui/material-ui/issues/34154 than remove
                                            // @ts-ignore
                                            "data-testid": `radio-input-paper`,
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                value={CommunicationType.EMAIL}
                                label={t("CustomerCard.Communication.Electronic")}
                                control={
                                    <Radio
                                        data-testid="radio-electronic"
                                        inputProps={{
                                            // Necessary, waiting https://github.com/mui/material-ui/issues/34154 than remove
                                            // @ts-ignore
                                            "data-testid": `radio-input-electronic`,
                                        }}
                                    />
                                }
                            />
                        </RadioGroup>
                    )}
                />
                <FieldError error={errors.officeCommunication?.channel} />
            </FormControl>
            <StyledCustomerTypography>{t("CustomerCard.Communication.WithYou")}</StyledCustomerTypography>
            <StyledCustomerTypography>{t("CustomerCard.Communication.Copies")}</StyledCustomerTypography>
            <Grid container marginBottom={2} marginTop={1}>
                <Grid item xs={12} sm={6}>
                    <TextInput
                        name="officeCommunication.address"
                        label={t("CustomerCard.Communication.SignAt")}
                        required
                        autoComplete="off"
                        control={control}
                    />
                </Grid>
                <GridLineBreak />
                <Grid item xs={12} sm={6}>
                    <DateInput
                        control={control}
                        name="officeCommunication.date"
                        label={t("CustomerCard.Communication.SignDate")}
                        required
                        disabled
                    />
                </Grid>
                <GridLineBreak />
                {!isPhysicalPerson && (
                    <Grid item xs={12} sm={6}>
                        <TextInput
                            name="officeCommunication.representativeInfo"
                            label={t("CustomerCard.Communication.RepresentativeInfo")}
                            required
                            autoComplete="off"
                            control={control}
                        />
                    </Grid>
                )}
            </Grid>
            <StyledCustomerTypography>{t("CustomerCard.Communication.Approved")}</StyledCustomerTypography>
        </FormPanel>
    );
};
export default CommunicationStep;
